/**
 * Gemensamma regler och texter för annonslyft, så att alla ställen i appen
 * beskriver lyften på exakt samma sätt.
 *
 * Så funkar ett lyft:
 * - Din kontonivå bestämmer steget, alltså minsta längd på ett lyft (dygn).
 * - Varje dygn kostar en lyftdag ur månadskvoten.
 * - Längden väljs alltid i hela steg: 1, 2, 3 ... steg.
 */

/** Längsta lyft som kan bokas vid ett tillfälle, oavsett hur stor kvoten är. */
export const MAX_PROMOTION_DAYS = 90;

/** Längder man kan välja: hela steg upp till kvoten, men aldrig över taket. */
export function promotionDayOptions(step: number, remaining: number): number[] {
  const s = Math.max(1, Math.round(step || 1));
  const cap = Math.min(Math.max(0, remaining), MAX_PROMOTION_DAYS);
  const count = Math.floor(cap / s);
  return Array.from({ length: count }, (_, i) => (i + 1) * s);
}

/** Klampar ett önskat antal dygn till hela steg inom kvot och tak. */
export function clampPromotionDays(step: number, remaining: number, wanted: number): number {
  const s = Math.max(1, Math.round(step || 1));
  const maxSteps = Math.floor(Math.min(Math.max(0, remaining), MAX_PROMOTION_DAYS) / s);
  if (maxSteps < 1) return 0;
  const steps = Math.min(Math.max(Math.round((wanted || s) / s), 1), maxSteps);
  return steps * s;
}

/** Längsta lyft man kan boka just nu, 0 om kvoten inte räcker till ett helt steg. */
export function maxPromotionDays(step: number, remaining: number): number {
  const options = promotionDayOptions(step, remaining);
  return options.length ? options[options.length - 1] : 0;
}

/**
 * Ett lyft kan aldrig vara längre än annonsen faktiskt visas.
 * Returnerar hur många dygn som får bokas med hänsyn till både kvot och annonsens livslängd.
 */
export function promotionCap(remaining: number, adDaysLeft?: number | null): number {
  const quota = Math.max(0, remaining);
  if (adDaysLeft == null) return quota;
  return Math.min(quota, Math.max(0, Math.floor(adDaysLeft)));
}

/** Kort förklaring av steget. Används överallt där lyft kan bokas. */
export function promotionStepNote(step: number): string {
  const s = Math.max(1, Math.round(step || 1));
  return `Ett lyft bokas i steg om ${s} dygn och varje dygn kostar en lyftdag ur din månadskvot.`;
}

/** Kvotrad: "12 av 21 lyftdagar kvar den här månaden · steg om 7 dygn." */
export function promotionQuotaLine(remaining: number, limit: number, step: number): string {
  const s = Math.max(1, Math.round(step || 1));
  return `${remaining} av ${limit} lyftdagar kvar den här månaden · steg om ${s} dygn per lyft`;
}
